import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { ImportPlainRules, PlainHead, PlainSchemaWalk, PlainVerdict } from "./ImportPlain";

// AppPlainSniff - looks INSIDE a chosen file, on this device, to say whether it is a WhatsApp database
// that needs no key (an iPhone's `ChatStorage.sqlite`). Every decision is `ImportPlain`'s; this class
// only reads the bytes it asks for.
//
// ⭐ A FEW KILOBYTES, NOT THE FILE. The header (100 bytes), then the pages of the database's own table
// list, one `Blob.slice` at a time - a 400 MB database is judged without loading it.
//
// ⚠ `FileReader`, NOT `blob.arrayBuffer()`: the house rule against promises (`AppUnlockRun`'s header).
// ⚠ ONE READ IN FLIGHT, each answered by a named handler; `Settle` answers the caller exactly once.

/**
 * The end of a sniff: usable (and which kind), or the sentence the subscriber sees. `File` is the very
 * object that was looked into, so a caller whose subscriber has chosen ANOTHER file meanwhile can
 * tell this answer is about the old one.
 */
export type PlainSniffDone = (Verdict: PlainVerdict, File: Blob) => void;

export class AppPlainSniff {
    private File: Blob;
    private Name: string;
    private Done: PlainSniffDone;
    private Reader: FileReader | null = null;
    private Walk: PlainSchemaWalk | null = null;
    private Page: number = 0;
    private Ended: boolean = false;

    public constructor(File: Blob, Name: string, Done: PlainSniffDone) {
        this.File = File;
        this.Name = Name;
        this.Done = Done;
    }

    public Start = (): void => {
        if (getCoreUtils().KErrorMode === true) { this.Refuse("WACommander cannot read a backup now."); return; }
        try {
            if (this.File === null || this.File === undefined) { this.Refuse("No file was chosen."); return; }
            const Size: string = ImportPlainRules.SizeRefusal(this.File.size);
            if (Size.length > 0) { this.Refuse(Size); return; }
            this.Read(0, ImportPlainRules.HeadBytes, this.OnHead);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPlainSniff.Start");
            this.Refuse("That file could not be read.");
        }
    };

    /** One slice of the file, answered to `Then`. */
    private Read = (From: number, Length: number, Then: () => void): void => {
        if (getCoreUtils().KErrorMode === true) { this.Refuse("WACommander cannot read a backup now."); return; }
        try {
            const Reader: FileReader = new FileReader();
            this.Reader = Reader;
            Reader.onerror = this.OnReadFail;
            Reader.onload = Then;
            Reader.readAsArrayBuffer(this.File.slice(From, From + Length));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPlainSniff.Read");
            this.Refuse("That file could not be read.");
        }
    };

    /** The bytes the last read answered, or null. */
    private Taken = (): Uint8Array | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (this.Reader === null) { return null; }
            const Raw: ArrayBuffer | null = this.Reader.result as ArrayBuffer | null;
            this.Reader = null;
            if (Raw === null || Raw === undefined) { return null; }
            return new Uint8Array(Raw);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPlainSniff.Taken");
            return null;
        }
    };

    private OnHead = (): void => {
        if (getCoreUtils().KErrorMode === true) { this.Refuse("WACommander cannot read a backup now."); return; }
        try {
            const Head: Uint8Array | null = this.Taken();
            if (Head === null) { this.Refuse(ImportPlainRules.EmptyWhy); return; }
            const Said: PlainHead = ImportPlainRules.HeadVerdict(Head, this.Name, this.File.size);
            if (Said.Ok !== true) { this.Refuse(nz(Said.Why)); return; }
            this.Walk = new PlainSchemaWalk(Said.PageSize, this.File.size);
            this.Step();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPlainSniff.OnHead");
            this.Refuse("That file could not be checked.");
        }
    };

    /** Read the next page the walk asks for, or answer with its verdict. */
    private Step = (): void => {
        if (getCoreUtils().KErrorMode === true) { this.Refuse("WACommander cannot read a backup now."); return; }
        try {
            if (this.Walk === null) { this.Refuse("That file could not be checked."); return; }
            const Walk: PlainSchemaWalk = this.Walk;
            this.Page = Walk.Next();
            if (this.Page === 0) { this.Settle(Walk.Verdict()); return; }
            this.Read(Walk.OffsetOf(this.Page), Walk.PageBytes(), this.OnPage);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPlainSniff.Step");
            this.Refuse("That file could not be checked.");
        }
    };

    private OnPage = (): void => {
        if (getCoreUtils().KErrorMode === true) { this.Refuse("WACommander cannot read a backup now."); return; }
        try {
            if (this.Walk === null) { this.Refuse("That file could not be checked."); return; }
            this.Walk.Take(this.Page, this.Taken());
            this.Step();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPlainSniff.OnPage");
            this.Refuse("That file could not be checked.");
        }
    };

    /**
     * ⛔ A FAILED READ IS THE SUBSCRIBER'S SITUATION, NOT A DEFECT (`AppUnlockRun.OnReadFail`'s rule): a
     * file moved between the pick and the read, or a permission the browser withdrew.
     */
    private OnReadFail = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: an expected condition (law 1, fate 1) answered as a sentence the
        // subscriber can act on; reporting it would latch the page over a file they can simply re-pick.
        this.Refuse("That file could not be read from this device. Choose it again.");
    };

    private Refuse = (Why: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - this builds the one answer the caller waits for.
        const Out: PlainVerdict = new PlainVerdict();
        Out.Why = Why;
        this.Settle(Out);
    };

    /** ⛔ ONE ANSWER. `FileReader` can fire both `error` and `load` on some failures. */
    private Settle = (Verdict: PlainVerdict): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - this is the only thing that answers the
        // caller, and a guard here would leave the page waiting on a chosen file for ever.
        try {
            if (this.Ended === true) { return; }
            this.Ended = true;
            this.Reader = null;
            this.Done(Verdict, this.File);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppPlainSniff.Settle");
        }
    };
}
